import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

export type UserPlan = 'free' | 'pro' | 'excellence';

interface PlanRestrictions {
  plan: UserPlan;
  canAccessCalendar: boolean;
  canAccessStats: boolean;
  canAccessTasks: boolean;
  canAccessInvoices: boolean;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  isAdmin: boolean;
}

type FeatureKey = 'calendar' | 'stats' | 'tasks' | 'invoices';

interface UsePlanRestrictionsReturn {
  restrictions: PlanRestrictions | null;
  loading: boolean;
  error: string | null;
  checkAccess: (feature: FeatureKey) => boolean;
}

export const usePlanRestrictions = (): UsePlanRestrictionsReturn => {
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Vérifier le rôle admin depuis user_profiles
  useEffect(() => {
    const checkAdminRole = async () => {
      if (!user || !isSupabaseConfigured || !supabase) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('role')
          .eq('user_id', user.id)
          .maybeSingle();

        if (profileError) {
          // Erreur 406 peut signifier RLS bloque ou profil n'existe pas
          if (profileError.code === 'PGRST406') {
            console.warn('Accès au profil bloqué par RLS (erreur 406):', profileError);
          } else {
            console.error('Error fetching user profile:', profileError);
          }
          setIsAdmin(false);
          setLoading(false);
          return;
        }

        const normalizedRole = profile?.role?.toLowerCase();
        const adminStatus = profile && ['admin', 'moderator'].includes(normalizedRole || '');
        setIsAdmin(adminStatus || false);
      } catch (err) {
        console.error('Error checking admin role:', err);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    checkAdminRole();
  }, [user]);

  // Restrictions par défaut
  const defaultRestrictions: PlanRestrictions = {
    plan: 'free',
    canAccessCalendar: true,
    canAccessStats: true,
    canAccessTasks: true,
    canAccessInvoices: true,
    isTrialActive: false,
    trialDaysRemaining: null,
    isAdmin: isAdmin
  };

  // Fonction pour vérifier l'accès à une fonctionnalité
  const checkAccess = (feature: FeatureKey): boolean => {
    // Admin a accès à tout
    if (isAdmin) return true;
    // Sinon, accès à tout par défaut
    return true;
  };

  return {
    restrictions: defaultRestrictions,
    loading,
    error: null,
    checkAccess
  };
};